/* eslint-disable no-console */
/* global FormData, navigator, MediaRecorder, Blob */
import React, { useState, useEffect, useRef } from "react"
import { Button, Tooltip, message } from "antd"
import voiceinputicon from "../../../assets/images/streamline_voice-mail.png"
import stopRecordingIcon from "../../../assets/images/stop-recording.png"
import recordingManager from "./RecordingManager"

const VoiceInput = ({
  onTranscriptionComplete,
  onRecordingStateChange,
  disabled,
  disabledTooltipMessage,
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [transcriptionStatus, setTranscriptionStatus] = useState("idle")
  const [isDisabledByOtherRecording, setIsDisabledByOtherRecording] = useState(false)
  const audioChunks = useRef([])
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const analyserDataRef = useRef(null)
  const silenceCheckIntervalRef = useRef(null)
  const initialSilenceTimeoutRef = useRef(null)
  const hasDetectedAudioRef = useRef(false)
  const abortReasonRef = useRef(null)
  // Ref to always hold the current mediaRecorder — avoids stale closure issues in timers
  const mediaRecorderRef = useRef(null)

  // Generate unique instance ID for this VoiceInput component
  const instanceId = useRef(
    `voice_input_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  )

  const stopRecording = () => {
    if (silenceCheckIntervalRef.current) {
      clearInterval(silenceCheckIntervalRef.current)
      silenceCheckIntervalRef.current = null
    }
    if (initialSilenceTimeoutRef.current) {
      clearTimeout(initialSilenceTimeoutRef.current)
      initialSilenceTimeoutRef.current = null
    }
    hasDetectedAudioRef.current = false
    abortReasonRef.current = null
    analyserRef.current = null
    analyserDataRef.current = null
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }

    if (
      mediaRecorderRef.current
      && mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop()
    }

    setIsRecording(false)
    setTranscriptionStatus("transcribing")

    // Notify RecordingManager that this instance has stopped recording and started transcribing
    recordingManager.recordingStopped(instanceId.current)
    recordingManager.setTranscribing(instanceId.current, true)
  }

  // Clean up on unmount
  useEffect(
    () => () => {
      if (silenceCheckIntervalRef.current) {
        clearInterval(silenceCheckIntervalRef.current)
        silenceCheckIntervalRef.current = null
      }
      if (initialSilenceTimeoutRef.current) {
        clearTimeout(initialSilenceTimeoutRef.current)
        initialSilenceTimeoutRef.current = null
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {})
        audioContextRef.current = null
      }
    },
    [],
  )

  // Register with RecordingManager and handle global recording state changes
  useEffect(() => {
    // Register this instance with the recording manager
    const stopRecordingCallback = () => {
      stopRecording()
    }
    recordingManager.registerInstance(
      instanceId.current,
      stopRecordingCallback,
    )

    // Listen for global recording state changes
    const handleGlobalRecordingChange = (recordingId, state) => {
      // Update disabled state based on whether another instance is recording or transcribing
      const shouldDisable = (state === true || state === "transcribing")
        && recordingId !== instanceId.current
      setIsDisabledByOtherRecording(shouldDisable)

      if (recordingId !== instanceId.current) {
        // Another instance started/stopped recording or transcribing
      }
    }

    // Initial check for disabled state
    setIsDisabledByOtherRecording(
      recordingManager.shouldDisableInstance(instanceId.current),
    )

    recordingManager.addListener(handleGlobalRecordingChange)

    // Cleanup on unmount
    return () => {
      recordingManager.unregisterInstance(instanceId.current)
      recordingManager.removeListener(handleGlobalRecordingChange)
    }
  }, [])

  // Notify parent of recording state changes
  useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(isRecording, transcriptionStatus)
    }
  }, [isRecording, transcriptionStatus, onRecordingStateChange])

  const validateAudioQuality = (audioBlob) => {
    // Reject blobs that are clearly too small to contain any speech
    // WebM/Opus at 64kbps ≈ 8KB/s, so 12KB ≈ ~1.5 seconds minimum
    const MIN_AUDIO_SIZE_BYTES = 12 * 1024

    if (audioBlob.size < MIN_AUDIO_SIZE_BYTES) {
      throw new Error(
        "Recording is too short or too quiet. Please ensure you are speaking clearly.",
      )
    }

    return true
  }

  const uploadAudioForTranscription = async (audioBlob) => {
    // Validate audio quality before uploading
    try {
      validateAudioQuality(audioBlob)
    } catch (error) {
      message.warning(error.message)
      throw error
    }

    const formData = new FormData()
    const filename = audioBlob.type === "audio/wav" ? "recording.wav" : "recording.webm"
    formData.append("audio", audioBlob, filename)

    // Get CSRF token
    // const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const metaTag = document.querySelector("meta[name=\"csrf-token\"]")
    let csrfToken = null

    if (metaTag) {
      csrfToken = metaTag.getAttribute("content")
    }
    const headers = {
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    }

    const response = await fetch("/api/v1/transcribe_audio", {
      method: "POST",
      headers,
      body: formData,
      credentials: "same-origin",
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Transcription failed: ${response.statusText}${
          errorText ? ` - ${errorText}` : ""
        }`,
      )
    }

    const result = await response.json()

    if (result.error) {
      throw new Error(result.error)
    }

    return result.transcription
  }

  const startRecording = async () => {
    // Check if this instance should be disabled due to another recording or transcription
    if (recordingManager.shouldDisableInstance(instanceId.current)) {
      message.info(
        "Another voice recording or transcription is in progress. Please wait for it to complete.",
      )
      return
    }

    abortReasonRef.current = null
    hasDetectedAudioRef.current = false

    // Request permission from RecordingManager to start recording
    const canStart = recordingManager.requestStartRecording(instanceId.current)
    if (!canStart) {
      return
    }

    // Clear any previous audio data
    audioChunks.current = []

    try {
      // Request microphone access with specific constraints
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true, // Re-enable to boost quiet audio
          sampleRate: 16000,
          channelCount: 1, // Force mono
          deviceId: "default", // Try to get default mic
        },
      }

      // Get available audio devices first
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      )

      // If no device labels available, request permissions first
      if (audioInputs.length > 0 && audioInputs[0].label === "") {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        tempStream.getTracks().forEach((track) => track.stop())

        // Re-enumerate devices after permission granted
        const devices2 = await navigator.mediaDevices.enumerateDevices()
        audioInputs.length = 0
        audioInputs.push(
          ...devices2.filter((device) => device.kind === "audioinput"),
        )
      }

      // Try to find a real microphone (avoid system audio)
      let selectedDeviceId = null
      audioInputs.forEach((device) => {
        if (
          device.label
          && !device.label.toLowerCase().includes("default")
          && !device.label.toLowerCase().includes("system")
        ) {
          selectedDeviceId = device.deviceId
        }
      })

      if (selectedDeviceId) {
        constraints.audio.deviceId = selectedDeviceId
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)

      const teardownAudioMonitor = () => {
        if (silenceCheckIntervalRef.current) {
          clearInterval(silenceCheckIntervalRef.current)
          silenceCheckIntervalRef.current = null
        }
        if (initialSilenceTimeoutRef.current) {
          clearTimeout(initialSilenceTimeoutRef.current)
          initialSilenceTimeoutRef.current = null
        }
        analyserRef.current = null
        analyserDataRef.current = null
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {})
          audioContextRef.current = null
        }
      }

      // Create MediaRecorder instance with better audio settings
      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 64000, // Lower bitrate to reduce distortion
      })

      audioChunks.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data)
        }
      }

      recorder.onstop = async () => {
        try {
          teardownAudioMonitor()

          // Stop all tracks to release the microphone
          stream.getTracks().forEach((track) => {
            track.stop()
          })

          if (abortReasonRef.current === "initial_silence") {
            abortReasonRef.current = null
            audioChunks.current = []
            setTranscriptionStatus("idle")
            return
          }

          // Create audio blob from chunks
          const audioBlob = new Blob(audioChunks.current, {
            type: "audio/webm;codecs=opus",
          })

          setTranscriptionStatus("transcribing")

          // Upload audio for transcription
          const transcription = await uploadAudioForTranscription(audioBlob)

          if (
            transcription
            && typeof transcription === "string"
            && transcription.trim()
          ) {
            onTranscriptionComplete(transcription.trim())
            setTranscriptionStatus("completed")

            // Notify RecordingManager that transcription is complete
            recordingManager.setTranscribing(instanceId.current, false)

            // Reset status after a short delay
            setTimeout(() => {
              setTranscriptionStatus("idle")
            }, 2000)
          } else {
            message.info(
              "No speech detected. Click the microphone to try again.",
            )
            setTranscriptionStatus("idle")

            // Notify RecordingManager that transcription is complete (even if no speech detected)
            recordingManager.setTranscribing(instanceId.current, false)
          }
        } catch (error) {
          // Validation errors (too short/quiet) are already shown via message.warning
          // so we just reset to idle without showing another error
          if (
            error.message.includes("too short")
            || error.message.includes("too quiet")
          ) {
            setTranscriptionStatus("idle")
          } else {
            message.error(`Transcription failed: ${error.message}`)
            setTranscriptionStatus("error")
            setTimeout(() => setTranscriptionStatus("idle"), 3000)
          }

          // Notify RecordingManager that transcription is done
          recordingManager.setTranscribing(instanceId.current, false)
        }
      }

      recorder.onerror = (event) => {
        message.error(`Recording error: ${event.error}`)
        setIsRecording(false)
        setTranscriptionStatus("error")

        teardownAudioMonitor()

        // Notify RecordingManager that recording failed/stopped
        recordingManager.recordingStopped(instanceId.current)

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop())

        setTimeout(() => {
          setTranscriptionStatus("idle")
        }, 3000)
      }

      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass()
          const source = audioContextRef.current.createMediaStreamSource(stream)
          analyserRef.current = audioContextRef.current.createAnalyser()
          analyserRef.current.fftSize = 2048
          analyserDataRef.current = new Uint8Array(analyserRef.current.fftSize)
          source.connect(analyserRef.current)

          hasDetectedAudioRef.current = false
          const CHECK_INTERVAL_MS = 250
          const INITIAL_SILENCE_MS = 15000
          const RMS_THRESHOLD = 0.01

          silenceCheckIntervalRef.current = setInterval(() => {
            if (!analyserRef.current || !analyserDataRef.current) return
            if (hasDetectedAudioRef.current) return
            analyserRef.current.getByteTimeDomainData(analyserDataRef.current)
            let sumSquares = 0
            for (let i = 0; i < analyserDataRef.current.length; i += 1) {
              const v = (analyserDataRef.current[i] - 128) / 128
              sumSquares += v * v
            }
            const rms = Math.sqrt(sumSquares / analyserDataRef.current.length)
            if (rms > RMS_THRESHOLD) {
              hasDetectedAudioRef.current = true
              if (initialSilenceTimeoutRef.current) {
                clearTimeout(initialSilenceTimeoutRef.current)
                initialSilenceTimeoutRef.current = null
              }
              if (silenceCheckIntervalRef.current) {
                clearInterval(silenceCheckIntervalRef.current)
                silenceCheckIntervalRef.current = null
              }
            }
          }, CHECK_INTERVAL_MS)

          initialSilenceTimeoutRef.current = setTimeout(() => {
            if (hasDetectedAudioRef.current) return
            if (recorder.state !== "recording") return

            abortReasonRef.current = "initial_silence"
            setIsRecording(false)
            setTranscriptionStatus("idle")

            recordingManager.recordingStopped(instanceId.current)
            recordingManager.setTranscribing(instanceId.current, false)

            message.info("No audio detected. Please try again.")

            stream.getTracks().forEach((track) => track.stop())
            try {
              recorder.stop()
            } catch (e) {
              // Ignore errors when stopping recorder
            }
          }, INITIAL_SILENCE_MS)
        }
      } catch (e) {
        // Ignore errors in audio monitoring setup
      }

      // Start recording
      recorder.start(1000) // Collect data every 1 second
      mediaRecorderRef.current = recorder
      setIsRecording(true)
    } catch (error) {
      // Notify RecordingManager that recording failed/stopped
      recordingManager.recordingStopped(instanceId.current)

      message.error(
        "Failed to start recording. Please check microphone permissions.",
      )
    }
  }

  const handleVoiceRecordingClick = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  return (
    <>
      <Tooltip
        title={
          disabled
            ? disabledTooltipMessage || "Voice Input Disabled During AI Enhancement"
            : isDisabledByOtherRecording
              ? "Another voice recording or transcription is in progress"
              : isRecording
                ? "Stop Recording"
                : transcriptionStatus === "transcribing"
                  ? "Transcribing audio..."
                  : "Start Voice Recording"
        }
        placement="top"
      >
        {disabled
        || transcriptionStatus === "transcribing"
        || isDisabledByOtherRecording ? (
          <span
            className="indicator-toolbar-icon-btn-ai ant-tooltip-disabled-compatible-wrapper"
            style={{
              // display: "inline-block",
              cursor: "not-allowed",
              width: "34px !important",
              height: "34px !important",
              opacity: isDisabledByOtherRecording ? 0.5 : 1,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              disabled
              className="ant-btn"
              style={{
                opacity: isDisabledByOtherRecording ? 0.5 : 1,
                cursor: "not-allowed",
                borderRadius: "8px",
                width: "34px",
                height: "34px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {transcriptionStatus === "transcribing" ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ fontSize: "18px" }}
                />
              ) : isDisabledByOtherRecording ? (
                <i
                  className="fa fa-microphone-slash"
                  style={{ fontSize: "18px", color: "#ff4d4f" }}
                />
              ) : (
                <img
                  src={voiceinputicon}
                  alt="Voice Input"
                  className="indicator-toolbar-icon-voice"
                />
              )}
            </button>
          </span>
          ) : (
            <Button
            // type="default"
              onClick={handleVoiceRecordingClick}
              className="indicator-toolbar-icon-btn"
            >
              {transcriptionStatus === "transcribing" ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ fontSize: "18px" }}
                />
              ) : (
                <img
                  src={isRecording ? stopRecordingIcon : voiceinputicon}
                  alt={isRecording ? "Stop Recording" : "Voice Input"}
                  className={
                  isRecording
                    ? "indicator-toolbar-icon-stop"
                    : "indicator-toolbar-icon-voice"
                }
                />
              )}
            </Button>
          )}
      </Tooltip>

      {/* Transcription status messages */}
      {/* {transcriptionStatus === "transcribing" && (
        <div className="indicator-transcription-status indicator-transcription-transcribing">
          <i className="fa fa-spinner fa-spin" /> Transcribing audio...
        </div>
      )}

      {transcriptionStatus === "completed" && (
        <div className="indicator-transcription-status indicator-transcription-completed">
          <i className="fa fa-check-circle" /> Transcription completed!
        </div>
      )} */}

      {/* Only show error in toolbar */}
      {transcriptionStatus === "error" && (
        <div className="indicator-transcription-status indicator-transcription-error">
          <i className="fa fa-exclamation-circle" /> Transcription failed.
          Please try again.
        </div>
      )}
    </>
  )
}

export default VoiceInput
